import { useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { useHeaderSearch } from "../lib/headerSearch";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { PERSON_STANDINGS, can } from "@family/shared";
import type {
  Person,
  PersonStanding,
  TreeAuditPerson,
  TreeAuditResponse,
  TreePlacement,
} from "@family/shared";

type Tab = "review" | "unlinked" | "outside" | "detached" | "recent";

export const STANDING_LABEL: Record<PersonStanding, string> = {
  family: "Family",
  married_in: "Married in",
  related_by_marriage: "Related by marriage",
  friend: "Friend of the family",
  not_family: "Not a relative",
};

export const STANDING_HINT: Record<PersonStanding, string> = {
  family:
    "A blood relative, descended from the founder. Pick this alongside connecting them in the tree — saying \"family\" does not link anyone.",
  married_in: "Married a family member. Belongs in the tree.",
  related_by_marriage:
    "An in-law's own relative — a spouse's parent or sibling. A real relative of a member, but not of this family.",
  friend: "A friend of the family, not a relative.",
  not_family:
    "Appears in our photos or stories but is neither a relative nor a friend — a dignitary, a colleague, someone in a group photo.",
};

const PLACEMENT_LABEL: Record<TreePlacement, string> = {
  blood: "In the family",
  married_in: "Married in",
  outside: "Outside the family",
  detached: "Not connected",
  unlinked: "Not linked",
};

const PLACEMENT_HINT: Record<TreePlacement, string> = {
  blood: "A descendant of the founder (or one of their ancestors).",
  married_in: "Married to someone in the family — belongs in the tree.",
  outside:
    "Connected to the tree, but not through the family line. Usually a relative of someone who married in.",
  detached:
    "Has relationships, but the whole group is cut off from the rest of the tree.",
  unlinked: "Added with “Add person”, then never linked to anyone.",
};

/** Claimed as part of the tree, but the relationships do not put them there.
 *  An unfinished job rather than a decision, so the row says so. */
function needsLinking(p: TreeAuditPerson): boolean {
  if (p.standing !== "family" && p.standing !== "married_in") return false;
  return p.placement !== "blood" && p.placement !== "married_in";
}

function fullName(p: TreeAuditPerson): string {
  const base = [p.givenName, p.familyName].filter(Boolean).join(" ");
  return p.nickname ? `${base} “${p.nickname}”` : base;
}

function years(p: TreeAuditPerson): string {
  const b = p.birthDate?.slice(0, 4);
  const d = p.deathDate?.slice(0, 4);
  if (!b && !d) return "";
  return ` (${b ?? "?"}–${d ?? ""})`;
}

function addedLine(p: TreeAuditPerson): string {
  const when = new Date(p.createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return p.createdByName
    ? `Added ${when} by ${p.createdByName}`
    : `Added ${when} — author not recorded (added before the app started keeping it)`;
}

export function TreeAuditPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;
  const allowed = Boolean(primary) && can(primary!.role, "tree:edit");

  const auditQuery = useQuery<TreeAuditResponse>({
    queryKey: ["tree-audit", slug],
    queryFn: () => apiFetch<TreeAuditResponse>(`/families/${slug}/tree-audit`),
    enabled: Boolean(slug) && allowed,
  });

  const [tab, setTab] = useState<Tab>("review");
  const [filter, setFilter] = useState("");
  const filterRef = useRef<HTMLInputElement>(null);
  useHeaderSearch("the tree audit", () => {
    filterRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    filterRef.current?.focus();
  });

  const data = auditQuery.data;
  const shown = useMemo(() => {
    if (!data) return [];
    const source =
      tab === "recent"
        ? data.recent
        : tab === "review"
          ? data.flagged
          : data.flagged.filter((p) =>
              tab === "unlinked"
                ? p.placement === "unlinked"
                : tab === "outside"
                  ? p.placement === "outside"
                  : p.placement === "detached",
            );
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return source;
    return source.filter((p) => {
      const hay = [
        p.givenName,
        p.familyName,
        p.nickname,
        p.createdByName,
        p.connectedVia?.name,
      ]
        .filter((v): v is string => Boolean(v))
        .join(" ")
        .toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [data, tab, filter]);

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !allowed) return <Navigate to="/" replace />;

  const reviewCount = data?.flagged.length ?? 0;

  // The sub-tabs slice the review queue by what the graph says, which is the
  // useful grouping when deciding a standing: everyone "outside" tends to be
  // one in-law's relatives, everyone "not linked" tends to be one session.
  const inQueue = (pl: TreePlacement) =>
    data?.flagged.filter((p) => p.placement === pl).length ?? null;
  const tabs: Array<{ key: Tab; label: string; n: number | null }> = [
    { key: "review", label: "Needs review", n: reviewCount },
    { key: "unlinked", label: "Not linked", n: inQueue("unlinked") },
    { key: "outside", label: "Outside the family", n: inQueue("outside") },
    { key: "detached", label: "Not connected", n: inQueue("detached") },
    { key: "recent", label: "Recently added", n: data?.recent.length ?? null },
  ];

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>Tree audit — {primary.familyName}</h1>
          <Link to="/tree" className="btn-secondary" style={{ textDecoration: "none" }}>
            Open the tree
          </Link>
        </div>

        <p className="text-muted" style={{ margin: 0, maxWidth: "60ch" }}>
          Everyone on record, measured against the family line
          {data?.founderName ? ` from ${data.founderName}` : ""}. Say what each
          person is and they leave this list — including “related by marriage”
          for an in-law's own parents, who are correctly recorded and are not
          a mistake to fix. Only <strong>Family</strong> and{" "}
          <strong>Married in</strong> count towards the tree, and only an
          explicit non-family standing takes someone out of the tree view.
          Marking somebody <strong>Family</strong> does not connect them: they
          stay on this list until their relationships say so.
        </p>

        {data && !data.rootIsExplicit && (
          <p
            className="card"
            style={{
              margin: 0,
              padding: "10px 16px",
              borderColor: "var(--c-danger)",
              fontSize: "0.9rem",
            }}
          >
            <strong>Read this first.</strong> No founder is set for this
            family, so the page guessed{" "}
            <strong>{data.founderName ?? "nobody"}</strong> — the parentless
            person with the most descendants. On a tree that reaches above the
            founder, or one where an in-law's line is long, the guess lands on
            the wrong person and the lists below invert. Set the founder under{" "}
            <Link to="/settings">Settings → Branch moderators</Link> before
            acting on anything here.
          </p>
        )}

        {auditQuery.isLoading && <p className="text-muted">Working out the tree…</p>}
        {auditQuery.error && (
          <p className="text-error">Error: {(auditQuery.error as Error).message}</p>
        )}

        {data && (
          <>
            <ul
              className="list-unstyled"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--s-3)",
                margin: 0,
              }}
            >
              <Stat
                label="In the family tree"
                value={data.inTree}
                detail={`${data.counts.family} family + ${data.counts.marriedIn} married in`}
                total
              />
              <Stat label="Family" value={data.counts.family} />
              <Stat label="Married in" value={data.counts.marriedIn} />
              <Stat
                label="Related by marriage"
                value={data.counts.relatedByMarriage}
              />
              <Stat label="Friends of the family" value={data.counts.friend} />
              <Stat label="Not a relative" value={data.counts.notFamily} />
              <Stat label="Not reviewed" value={data.counts.unreviewed} warn />
              <Stat
                label="People on record"
                value={data.total}
                detail="everyone, in the tree or not"
                total
              />
            </ul>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--s-2)",
                alignItems: "center",
              }}
            >
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={tab === t.key ? "" : "btn-secondary"}
                  onClick={() => setTab(t.key)}
                  style={{ padding: "6px 14px", fontSize: "0.85rem", minHeight: 34 }}
                >
                  {t.label}
                  {t.n !== null && ` (${t.n})`}
                </button>
              ))}
              <input
                ref={filterRef}
                className="member-filter"
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by name or who added them…"
                aria-label="Filter the tree audit"
              />
            </div>

            {tab !== "recent" && shown.length === 0 && (
              <p className="text-muted">
                Nothing here — everyone in this group has been given a standing.
              </p>
            )}

            {tab === "recent" && (
              <p className="text-subtle" style={{ fontSize: "0.85rem", margin: 0 }}>
                The {data.recent.length} most recently added people, whatever
                their placement — this is where unexpected growth shows up.
                People added before this release have no recorded author.
              </p>
            )}

            <ul className="list-unstyled stack" style={{ gap: "var(--s-2)" }}>
              {shown.map((p) => (
                <AuditRow key={p.id} person={p} familySlug={primary.familySlug} />
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  );
}

function Stat({
  label,
  value,
  detail,
  warn = false,
  total = false,
}: {
  label: string;
  value: number;
  /** Shown under the label — used to spell out a sum, because a total
   *  sitting in a row of equal-looking cards reads as a contradiction. */
  detail?: string;
  warn?: boolean;
  /** Renders as the figure the others break down, not a sibling of them. */
  total?: boolean;
}) {
  return (
    <li
      className="card"
      style={{
        padding: "10px 16px",
        minWidth: 120,
        background: total ? "var(--c-bg-alt)" : undefined,
        borderColor: warn && value > 0 ? "var(--c-danger)" : undefined,
      }}
    >
      <div
        style={{
          fontSize: total ? "1.9rem" : "1.5rem",
          fontWeight: 700,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        className="text-subtle"
        style={{ fontSize: "0.8rem", fontWeight: total ? 600 : undefined }}
      >
        {label}
      </div>
      {detail && (
        <div className="text-subtle" style={{ fontSize: "0.75rem", opacity: 0.8 }}>
          {detail}
        </div>
      )}
    </li>
  );
}

/** The picker that takes a person off the review queue. */
function StandingPicker({
  person: p,
  familySlug,
}: {
  person: TreeAuditPerson;
  familySlug: string;
}) {
  const queryClient = useQueryClient();
  const setStanding = useMutation<Person, Error, PersonStanding | null>({
    mutationFn: (standing) =>
      apiFetch<Person>(`/families/${familySlug}/persons/${p.id}/standing`, {
        method: "PUT",
        body: JSON.stringify({ standing }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tree-audit", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["persons", familySlug] });
    },
  });

  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
      <select
        className="select-skapa"
        aria-label={`Standing for ${fullName(p)}`}
        value={p.standing ?? ""}
        disabled={setStanding.isPending}
        onChange={(e) =>
          setStanding.mutate((e.target.value || null) as PersonStanding | null)
        }
        style={{ width: "auto", minHeight: 34, padding: "4px 10px", fontSize: "0.85rem" }}
      >
        <option value="">— not reviewed —</option>
        {PERSON_STANDINGS.map((v) => (
          <option key={v} value={v} title={STANDING_HINT[v]}>
            {STANDING_LABEL[v]}
          </option>
        ))}
      </select>
      {setStanding.error && (
        <span className="text-error" style={{ fontSize: "0.8rem" }}>
          {setStanding.error.message}
        </span>
      )}
    </div>
  );
}

function AuditRow({
  person: p,
  familySlug,
}: {
  person: TreeAuditPerson;
  familySlug: string;
}) {
  const extras: string[] = [];
  if (p.connectedVia) extras.push(`Connected through ${p.connectedVia.name}`);
  if (p.relationshipCount > 0) {
    extras.push(
      `${p.relationshipCount} relationship${p.relationshipCount === 1 ? "" : "s"}`,
    );
  }
  if (p.mediaTagCount > 0) {
    extras.push(
      `tagged in ${p.mediaTagCount} photo${p.mediaTagCount === 1 ? "" : "s"}`,
    );
  }
  if (p.linkedUserId) extras.push("linked to a member account");

  return (
    <li className="card row-between" style={{ padding: "10px 16px", gap: "var(--s-3)" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Link to={`/person/${p.id}`} style={{ fontWeight: 600 }}>
            {fullName(p)}
            {years(p)}
          </Link>
          <span className="badge" title={PLACEMENT_HINT[p.placement]}>
            {PLACEMENT_LABEL[p.placement]}
          </span>
          {p.standing && (
            <span className="badge" title={STANDING_HINT[p.standing]}>
              {STANDING_LABEL[p.standing]}
            </span>
          )}
        </div>
        <div className="text-subtle" style={{ fontSize: "0.85rem" }}>
          {addedLine(p)}
        </div>
        {needsLinking(p) && (
          <div style={{ fontSize: "0.85rem", color: "var(--c-danger)" }}>
            Marked {STANDING_LABEL[p.standing!].toLowerCase()}, but still not
            connected to the tree — use “Link in tree”. They stay on this list
            until someone does.
          </div>
        )}
        {p.standingSetByName && (
          <div className="text-subtle" style={{ fontSize: "0.85rem" }}>
            Standing set by {p.standingSetByName}
          </div>
        )}
        {extras.length > 0 && (
          <div className="text-subtle" style={{ fontSize: "0.85rem" }}>
            {extras.join(" · ")}
          </div>
        )}
      </div>
      <div style={{ display: "grid", gap: 6, justifyItems: "end" }}>
        <StandingPicker person={p} familySlug={familySlug} />
        {(p.placement === "unlinked" ||
          p.placement === "detached" ||
          needsLinking(p)) && (
          <Link
            to={`/tree?connect=${p.id}`}
            className="btn-secondary"
            style={{
              textDecoration: "none",
              padding: "6px 14px",
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
            }}
          >
            Link in tree
          </Link>
        )}
      </div>
    </li>
  );
}
